import { InfoCircleOutlined } from '@ant-design/icons';
import { Card, Space, Tabs, Tooltip } from 'antd';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import React, { useMemo } from 'react';
import { MonthlyTrend } from '../../../types/dashboard';
import { formatRupiah, glassCardStyle, MONTH_NAMES } from '../utils';

interface TrendsTabsProps {
  monthlyTrends: MonthlyTrend[];
  loading: boolean;
}

export const TrendsTabs: React.FC<TrendsTabsProps> = ({ monthlyTrends, loading }) => {
  const trendChartOptions = useMemo(() => {
    if (!monthlyTrends) return {};

    const categories = MONTH_NAMES;
    const omzetTargetData = MONTH_NAMES.map((_, i) => {
      const match = monthlyTrends.find(t => t.month === i + 1);
      return match ? match.omzet_target : 0;
    });
    const omzetRealizationData = MONTH_NAMES.map((_, i) => {
      const match = monthlyTrends.find(t => t.month === i + 1);
      return match ? match.omzet_realization : 0;
    });

    const leadsTargetData = MONTH_NAMES.map((_, i) => {
      const match = monthlyTrends.find(t => t.month === i + 1);
      return match ? match.leads_target : 0;
    });
    const leadsRealizationData = MONTH_NAMES.map((_, i) => {
      const match = monthlyTrends.find(t => t.month === i + 1);
      return match ? match.leads_realization : 0;
    });

    return {
      omzet: {
        chart: { 
          type: 'column', 
          height: 350, 
          backgroundColor: 'transparent',
          style: { fontFamily: 'inherit' }
        },
        title: { 
          text: 'Tren Omzet Bulanan (Target vs Realisasi)', 
          style: { fontSize: '14px', fontWeight: 'bold', color: '#1e293b' } 
        },
        xAxis: { 
          categories, 
          crosshair: true,
          labels: { style: { color: '#64748b' } },
          lineColor: '#cbd5e1'
        },
        yAxis: { 
          min: 0, 
          title: { text: 'Nominal (Rp)', style: { color: '#64748b' } },
          labels: { style: { color: '#64748b' } },
          gridLineColor: 'rgba(0, 0, 0, 0.04)',
          gridLineDashStyle: 'Dash'
        },
        tooltip: {
          shared: true,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderRadius: 8,
          borderWidth: 1,
          borderColor: '#e2e8f0',
          shadow: true,
          formatter: function (this: any) {
            let s = `<span style="font-size: 11px; color: #64748b; font-weight: bold;">${this.x}</span>`;
            this.points.forEach((point: any) => {
              s += `<br/><span style="color:${point.color}">\u25CF</span> ${point.series.name}: <span style="font-weight: bold; color: #0f172a;">${formatRupiah(point.y)}</span>`;
            });
            return s;
          }
        },
        plotOptions: {
          column: {
            borderRadius: 6,
            borderWidth: 0,
            dataLabels: { enabled: false },
            maxPointWidth: 24
          }
        },
        colors: ['#cbd5e1', '#7BAA18'], // Target Slate-Gray, Realization Brand Emerald-ish
        credits: { enabled: false },
        series: [
          { name: 'Target Omzet', data: omzetTargetData },
          { name: 'Realisasi Omzet', data: omzetRealizationData }
        ]
      },
      leads: {
        chart: { 
          type: 'spline', 
          height: 350, 
          backgroundColor: 'transparent',
          style: { fontFamily: 'inherit' }
        },
        title: { 
          text: 'Tren Perolehan Leads Bulanan (Target vs Realisasi)', 
          style: { fontSize: '14px', fontWeight: 'bold', color: '#1e293b' } 
        },
        xAxis: { 
          categories, 
          crosshair: true,
          labels: { style: { color: '#64748b' } },
          lineColor: '#cbd5e1'
        },
        yAxis: { 
          min: 0, 
          title: { text: 'Jumlah Leads', style: { color: '#64748b' } },
          labels: { style: { color: '#64748b' } },
          gridLineColor: 'rgba(0, 0, 0, 0.04)',
          gridLineDashStyle: 'Dash'
        },
        tooltip: {
          shared: true,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderRadius: 8,
          borderWidth: 1,
          borderColor: '#e2e8f0',
          shadow: true,
          pointFormat: '<span style="color:{series.color}">\u25CF</span> {series.name}: <span style="font-weight: bold; color: #0f172a;">{point.y}</span> leads<br/>'
        },
        plotOptions: {
          spline: {
            lineWidth: 3,
            marker: {
              enabled: true,
              radius: 4,
              symbol: 'circle'
            },
            dataLabels: { 
              enabled: true,
              style: { fontSize: '10px', color: '#475569', textOutline: 'none' }
            },
            enableMouseTracking: true
          }
        },
        colors: ['#faad14', '#1890ff'], // Warning Yellow for Target, Vibrant Blue for Realization
        credits: { enabled: false },
        series: [
          { name: 'Target Leads', data: leadsTargetData, marker: { symbol: 'circle' } },
          { name: 'Realisasi Leads', data: leadsRealizationData, marker: { symbol: 'circle' } }
        ]
      }
    };
  }, [monthlyTrends]);

  return (
    <Card bordered={false} style={glassCardStyle} bodyStyle={{ padding: 0 }} loading={loading}>
      <Tabs
        defaultActiveKey="leads"
        type="line"
        tabBarStyle={{ paddingLeft: '12px', borderBottom: '1px solid rgba(0, 0, 0, 0.05)' }}
        items={[
          {
            label: (
              <Space size={4}>
                <span>Tren Leads Bulanan</span>
                <Tooltip title="Grafik garis perbandingan Rencana Calon Pembeli (Target Leads) bulanan vs Realisasi Leads baru yang masuk pada bulan terkait sepanjang tahun untuk mengamati konsistensi kinerja perolehan lead. Grafik tren setahun penuh tetap utuh meskipun sedang memfilter bulan tertentu.">
                  <InfoCircleOutlined style={{ color: '#8c8c8c', fontSize: '11px', cursor: 'pointer' }} />
                </Tooltip>
              </Space>
            ),
            key: 'leads',
            children: (
              <div style={{ padding: '16px' }}>
                <HighchartsReact highcharts={Highcharts} options={trendChartOptions.leads} />
              </div>
            ),
          }, {
            label: (
              <Space size={4}>
                <span>Tren Omzet Bulanan</span>
                <Tooltip title="Grafik batang perbandingan akumulasi Omzet Rencana (Target) dari target bulanan sales agent aktif vs Realisasi Omzet riil transaksi closing approved pada bulan terkait sepanjang tahun. Grafik tren setahun penuh tetap utuh meskipun sedang memfilter bulan tertentu.">
                  <InfoCircleOutlined style={{ color: '#8c8c8c', fontSize: '11px', cursor: 'pointer' }} />
                </Tooltip>
              </Space>
            ),
            key: 'omzet',
            children: (
              <div style={{ padding: '16px' }}>
                <HighchartsReact highcharts={Highcharts} options={trendChartOptions.omzet} />
              </div>
            ),
          },
        ]}
      />
    </Card>
  );
};

export default TrendsTabs;
